import React, { useState, useEffect, useCallback } from "react";
import { getDolibarrSyncReport, retryDolibarrSync, updateDolibarrSyncSettings } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { RefreshCw, Loader2, Cloud, CloudOff, AlertTriangle, CheckCircle2, Clock, ExternalLink, ShieldAlert } from "lucide-react";
import { log } from "../../lib/log";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const StatusHeader = ({ r }) => (
  <div className={`rounded-xl border p-5 flex flex-wrap items-center gap-4 ${r.status.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="dolibarr-sync-status">
    {r.status.ok ? <Cloud className="text-emerald-600" size={28} /> : <CloudOff className="text-red-600" size={28} />}
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-neutral-900">{r.status.ok ? `Connexion Dolibarr OK · v${r.status.version}` : "Dolibarr injoignable"}</p>
      <p className="text-xs text-neutral-600 truncate">{r.status.ok ? r.url : r.status.error || "Vérifiez DOLIBARR_URL / DOLIBARR_API_KEY"}</p>
    </div>
    {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand flex items-center gap-1" data-testid="dolibarr-open-link"><ExternalLink size={12} /> Ouvrir Dolibarr</a>}
  </div>
);

const Block = ({ b }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`sync-block-${b.key}`}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <p className="font-semibold text-neutral-900 text-sm">{b.label}</p>
      {b.failed > 0 ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle size={11} /> {b.failed} en échec</span>
        : b.pending > 0 ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1"><Clock size={11} /> {b.pending} en attente</span>
        : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle2 size={11} /> À jour</span>}
    </div>
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="bg-neutral-50 rounded-lg py-2"><p className="text-lg font-bold text-neutral-900" data-testid={`sync-${b.key}-total`}>{b.total}</p><p className="text-[11px] text-neutral-500">Total</p></div>
      <div className="bg-emerald-50 rounded-lg py-2"><p className="text-lg font-bold text-emerald-700" data-testid={`sync-${b.key}-synced`}>{b.synced}</p><p className="text-[11px] text-neutral-500">Transmis</p></div>
      <div className={`${b.failed ? "bg-red-50" : "bg-neutral-50"} rounded-lg py-2`}><p className={`text-lg font-bold ${b.failed ? "text-red-700" : "text-neutral-400"}`} data-testid={`sync-${b.key}-failed`}>{b.failed}</p><p className="text-[11px] text-neutral-500">Échecs</p></div>
    </div>
    {b.errors.length > 0 && (
      <ul className="mt-3 space-y-1.5" data-testid={`sync-${b.key}-errors`}>
        {b.errors.map((e) => (
          <li key={e.id} className="text-xs bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5">
            <span className="font-semibold text-neutral-800">{e.label}</span>{e.when && <span className="text-neutral-500"> · {e.when}</span>}
            <p className="text-red-700 break-words">{e.error}</p>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function DolibarrSyncPanel({ isMaster }) {
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try { setReport((await getDolibarrSyncReport()).data); setErr(""); }
    catch (e) { log.error(e); setErr(e.response?.data?.detail || "Impossible de charger le rapport de synchronisation"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await retryDolibarrSync();
      const ok = data.prospects + data.interventions + data.appointments + data.absences + (data.tickets || 0);
      toast({ title: `Synchronisation relancée : ${ok} élément(s) transmis`, description: data.failed ? `${data.failed} toujours en échec` : "Aucun échec" });
      await load();
    } catch (e) { log.error(e); toast({ title: "Erreur", description: e.response?.data?.detail || "Relance impossible", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const toggleAuto = async (v) => {
    try { const { data } = await updateDolibarrSyncSettings({ auto_prospects: v }); setReport((r) => ({ ...r, settings: data })); toast({ title: v ? "Envoi automatique des prospects activé" : "Envoi automatique désactivé" }); }
    catch (e) { log.error(e); toast({ title: "Erreur", description: "Réglage non enregistré", variant: "destructive" }); }
  };

  if (err) return <p className="text-red-600 text-sm" data-testid="dolibarr-sync-error">{err}</p>;
  if (!report) return <p className="text-neutral-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Chargement…</p>;
  const last = report.retry?.last;

  return (
    <div className="space-y-5" data-testid="dolibarr-sync-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h2 className="text-lg font-bold text-neutral-900">Synchronisation Dolibarr</h2><p className="text-sm text-neutral-500">Contrôle des informations transmises à l'ERP et relance des envois en échec.</p></div>
        <div className="flex gap-2">
          <button onClick={load} className="border border-neutral-300 hover:bg-neutral-50 px-3 py-2 rounded-md text-sm flex items-center gap-2" data-testid="dolibarr-sync-refresh"><RefreshCw size={14} /> Actualiser</button>
          <button onClick={retry} disabled={busy} className="bg-brand hover:bg-brand-dark text-white font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60" data-testid="dolibarr-sync-retry">{busy ? <Loader2 className="animate-spin" size={14} /> : <Cloud size={14} />} Relancer la synchro</button>
        </div>
      </div>

      <StatusHeader r={report} />

      {report.waf_hint && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3" data-testid="dolibarr-waf-hint">
          <ShieldAlert size={18} className="shrink-0 mt-0.5" />
          <p>Des envois sont bloqués par le pare-feu o2switch (Tiger Protect). Dans cPanel → Tiger Protect, ajoutez l'IP du serveur CRM en liste blanche ou désactivez l'anti-bot pour le sous-domaine Dolibarr. Les envois sont retentés automatiquement toutes les {report.retry.interval_min} min.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{report.blocks.map((b) => <Block key={b.key} b={b} />)}</div>

      <div className="grid md:grid-cols-3 gap-4 text-sm">
        <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="sync-retry-info">
          <p className="font-semibold text-neutral-900 mb-1">Relance automatique</p>
          <p className="text-neutral-600 text-xs">Toutes les {report.retry.interval_min} min · dernière : {fmt(last?.at)}</p>
          {last && <p className="text-xs text-neutral-600 mt-1">Transmis : {last.prospects + last.interventions + last.appointments + last.absences + (last.tickets || 0)} · Échecs : {last.failed}</p>}
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="sync-team-info">
          <p className="font-semibold text-neutral-900 mb-1">Utilisateurs ← Dolibarr</p>
          <p className="text-neutral-600 text-xs">Dernière synchro : {fmt(report.team_sync?.last_sync)} · {report.team_sync?.count ?? "—"} utilisateur(s)</p>
          {report.team_sync?.error && <p className="text-xs text-red-600 mt-1">{report.team_sync.error}</p>}
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="sync-absences-info">
          <p className="font-semibold text-neutral-900 mb-1">Congés ← Dolibarr</p>
          <p className="text-neutral-600 text-xs">Dernière synchro : {fmt(report.absences_sync?.last_sync)} · {report.absences_sync?.count ?? "—"} congé(s)</p>
          {report.absences_sync?.error && <p className="text-xs text-red-600 mt-1">{report.absences_sync.error}</p>}
        </div>
      </div>

      <label className={`flex items-start gap-3 bg-white border border-neutral-200 rounded-xl p-4 text-sm ${isMaster ? "" : "opacity-70"}`} data-testid="sync-auto-prospects">
        <input type="checkbox" className="accent-brand w-4 h-4 mt-0.5" checked={report.settings.auto_prospects} disabled={!isMaster} onChange={(e) => toggleAuto(e.target.checked)} data-testid="sync-auto-prospects-toggle" />
        <span><span className="font-semibold text-neutral-900">Créer automatiquement un tiers Dolibarr pour chaque prospect qualifié</span><br />
          <span className="text-xs text-neutral-600">Un prospect avec email ou téléphone est transmis à Dolibarr dès sa création (saisie, recherche IA, prospection auto). Les prospects sans contact restent uniquement dans le CRM.</span></span>
      </label>
    </div>
  );
}
